import {
  apiDownload,
  apiGet,
  apiPost,
  buildApiRoute,
  toQueryParams,
} from "./entityApiShared.js";

import { cmtsDashboardReportApiRoutes } from "../core/api/cmtsDashboardReportApiRoutes.js";

import type {
  EntityCreateRequest,
  EntityRecord,
  PageQuery,
} from "../types/api.types.js";

export type DashboardQuery =
  PageQuery & {
    readonly filters?:
      | Record<
          string,
          string | number | boolean | null | undefined
        >
      | undefined;
  };

export type DashboardSummaryDto = EntityRecord;
export type DashboardDrilldownDto = EntityRecord;
export type DashboardExportRequest =
  EntityCreateRequest;

/*
 * Important:
 * இந்த function dashboardRouteFor function-க்கு வெளியே
 * இருக்க வேண்டும்.
 */
function isRecord(
  value: unknown,
): value is EntityRecord {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value)
  );
}

function dashboardRouteFor(
  type: string,
) {
  const normalized =
    type.trim().toLowerCase();

  if (normalized === "executive") {
    return cmtsDashboardReportApiRoutes
      .dashboard.executive;
  }

  if (normalized === "personal") {
    return cmtsDashboardReportApiRoutes
      .dashboard.personal;
  }

  if (
    normalized === "complaint-lifecycle" ||
    normalized === "complaintlifecycle"
  ) {
    return cmtsDashboardReportApiRoutes
      .dashboard.complaintLifecycle;
  }

  if (
    normalized === "rca-capa" ||
    normalized === "rcacapa"
  ) {
    return cmtsDashboardReportApiRoutes
      .dashboard.rcaCapa;
  }

  if (normalized === "sla") {
    return cmtsDashboardReportApiRoutes
      .dashboard.sla;
  }

  if (normalized === "evidence") {
    return cmtsDashboardReportApiRoutes
      .dashboard.evidence;
  }

  if (normalized === "summary") {
    return cmtsDashboardReportApiRoutes
      .dashboard.summary;
  }

  return cmtsDashboardReportApiRoutes
    .dashboard.management;
}

export const dashboardApi = {
  listDashboardMetrics: (
    query?: DashboardQuery,
    signal?: AbortSignal,
  ): Promise<DashboardSummaryDto> =>
    apiGet<DashboardSummaryDto>(
      buildApiRoute(
        cmtsDashboardReportApiRoutes
          .dashboard.summary,
        {},
        {
          query: toQueryParams(query),
        },
      ),
      { signal },
    ),

  getDashboardSummary: (
    query?: DashboardQuery,
    signal?: AbortSignal,
  ): Promise<DashboardSummaryDto> =>
    apiGet<DashboardSummaryDto>(
      buildApiRoute(
        cmtsDashboardReportApiRoutes
          .dashboard.summary,
        {},
        {
          query: toQueryParams(query),
        },
      ),
      { signal },
    ),

  getExecutiveDashboard: (
    query?: DashboardQuery,
    signal?: AbortSignal,
  ): Promise<DashboardSummaryDto> =>
    apiGet<DashboardSummaryDto>(
      buildApiRoute(
        cmtsDashboardReportApiRoutes
          .dashboard.executive,
        {},
        {
          query: toQueryParams(query),
        },
      ),
      { signal },
    ),

  getDashboardByType: (
    dashboardType: string,
    query?: DashboardQuery,
    signal?: AbortSignal,
  ): Promise<DashboardSummaryDto> =>
    apiGet<DashboardSummaryDto>(
      buildApiRoute(
        dashboardRouteFor(dashboardType),
        {},
        {
          query: toQueryParams(query),
        },
      ),
      { signal },
    ),

  getDashboardChartData: async (
    chartKey: string,
    query?: DashboardQuery,
    signal?: AbortSignal,
  ): Promise<EntityRecord> => {
    const response =
      await apiGet<EntityRecord>(
        buildApiRoute(
          cmtsDashboardReportApiRoutes
            .dashboard.chartData,
          {
            chartKey,
          },
          {
            query: toQueryParams(query),
          },
        ),
        { signal },
      );

    /*
     * apiGet full API response envelope return செய்தால்
     * data property-ஐ unwrap செய்யும்.
     *
     * apiGet ஏற்கனவே unwrap செய்திருந்தால் response-ஐ
     * நேரடியாக பயன்படுத்தும்.
     */
    const payload =
      isRecord(response.data)
        ? response.data
        : response;

    const charts = payload.charts;

    /*
     * Backend DashboardQueryResult உள்ளே charts array
     * return செய்கிறது.
     *
     * Chart panel direct chart object எதிர்பார்ப்பதால்
     * முதல் chart object மட்டும் return செய்கிறோம்.
     */
    if (
      Array.isArray(charts) &&
      charts.length > 0
    ) {
      const firstChart = charts[0];

      if (isRecord(firstChart)) {
        return firstChart;
      }
    }

    /*
     * Backend direct chart object return செய்தாலும்
     * இந்த fallback அதை support செய்யும்.
     */
    return payload;
  },

  getDashboardMetricDetail: (
    metricKey: string,
    query?: DashboardQuery,
    signal?: AbortSignal,
  ): Promise<EntityRecord> =>
    apiGet<EntityRecord>(
      buildApiRoute(
        cmtsDashboardReportApiRoutes
          .dashboard.metricDetail,
        {
          metricKey,
        },
        {
          query: toQueryParams(query),
        },
      ),
      { signal },
    ),

  getDashboardDrilldown: (
    metricKey: string,
    query?: DashboardQuery,
    signal?: AbortSignal,
  ): Promise<DashboardDrilldownDto> =>
    apiGet<DashboardDrilldownDto>(
      buildApiRoute(
        cmtsDashboardReportApiRoutes
          .dashboard.drilldown,
        {},
        {
          query: toQueryParams({
            ...(query ?? {}),
            filters: {
              ...(query?.filters ?? {}),
              metricKey,
            },
          }),
        },
      ),
      { signal },
    ),

  management: (
    query?: DashboardQuery,
    signal?: AbortSignal,
  ): Promise<DashboardSummaryDto> =>
    apiGet<DashboardSummaryDto>(
      buildApiRoute(
        cmtsDashboardReportApiRoutes
          .dashboard.management,
        {},
        {
          query: toQueryParams(query),
        },
      ),
      { signal },
    ),

  personal: (
    query?: DashboardQuery,
    signal?: AbortSignal,
  ): Promise<DashboardSummaryDto> =>
    apiGet<DashboardSummaryDto>(
      buildApiRoute(
        cmtsDashboardReportApiRoutes
          .dashboard.personal,
        {},
        {
          query: toQueryParams(query),
        },
      ),
      { signal },
    ),

  drilldown: (
    query?: DashboardQuery,
    signal?: AbortSignal,
  ): Promise<DashboardDrilldownDto> =>
    apiGet<DashboardDrilldownDto>(
      buildApiRoute(
        cmtsDashboardReportApiRoutes
          .dashboard.drilldown,
        {},
        {
          query: toQueryParams(query),
        },
      ),
      { signal },
    ),

  export: (
    payload: DashboardExportRequest,
    signal?: AbortSignal,
  ): Promise<Blob> =>
    apiDownload(
      buildApiRoute(
        cmtsDashboardReportApiRoutes
          .dashboard.drilldown,
      ),
      {
        method: "POST",
        body: payload,
        signal,
      },
    ),

  exportJob: (
    payload: DashboardExportRequest,
    idempotencyKey?: string,
  ): Promise<EntityRecord> =>
    apiPost<EntityRecord>(
      buildApiRoute(
        cmtsDashboardReportApiRoutes
          .dashboard.drilldown,
      ),
      payload,
      {
        idempotencyKey,
      },
    ),
};